import React from "react";
import { useNavigate } from "react-router-dom";
import HighlightCard from "../HighlightCard";
import { PiDropFill } from "react-icons/pi";
import { FaCheck } from "react-icons/fa";
import { useQuery } from "@tanstack/react-query";
import { getCardCount, callWithAuth } from "@/api/Api";
import { useUser } from "@/hooks/UseAuthProviders";

export const REVIEW_CARD_COUNT_QUERY_KEY = "reviewCardsStats";

const ReviewInsights: React.FC = () => {
  let variant: "blue" | "green";
  let icon: JSX.Element;
  let title: JSX.Element;
  let subtitle: JSX.Element;
  let progressStyle: React.CSSProperties = {};
  const nav = useNavigate();
  const user = useUser();

  const reviewCountQuery = useQuery({
    queryKey: [REVIEW_CARD_COUNT_QUERY_KEY],
    queryFn: async () => {
      return await callWithAuth(getCardCount);
    },
    retry: 5,
    retryDelay: () => 200,
    refetchOnWindowFocus: false,
  });

  const dailyReviews = 30;
  const reviewCount: number = reviewCountQuery.data?.count || 0;

  const reviewsLeft = reviewCount;

  const progressVal = ((dailyReviews - reviewsLeft) / dailyReviews) * 100;

  if (reviewsLeft === 0) {
    variant = "green";
    icon = <FaCheck className="w-8 h-8 text-white" />;
    title = (
      <>
        <span className="font-bold">Great job!</span> You reviewed all your
        words today
      </>
    );
    subtitle = <>Check back tomorrow for more!</>;
  } else {
    variant = "blue";
    icon = <PiDropFill className="w-8 h-8 text-white" />;
    title = (
      <>
        You have <span className="font-bold">{reviewsLeft} words</span> left to
        review today
      </>
    );
    subtitle = (
      <>
        Press the icon <span className="font-semibold">to review now!</span>
      </>
    );
    progressStyle = {
      borderRadius: "100%",
      boxShadow:
        "0 10px 20px rgba(0, 0, 0, 0.22), 0 6px 6px rgba(0, 0, 0, 0.22)",
      cursor: "pointer",
    };
  }

  const handleClick = async () => {
    const adminBypass = user.user?.role === "admin";
    if (reviewsLeft !== 0 || adminBypass) {
      nav("/review");
    }
  };

  return (
    <HighlightCard
      title={title}
      subtitle={subtitle}
      variant={variant}
      progressVal={progressVal}
      progressStyle={progressStyle}
      icon={icon}
      onClick={handleClick}
    />
  );
};

export default ReviewInsights;
